import { motion } from "framer-motion";
import { ScrollText, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function TermsAndConditions() {
  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <div className="flex items-center gap-4 mb-10">
            <Button variant="ghost" size="icon" className="border border-primary/20 hover:bg-primary/10" asChild>
              <a href="#/"><ArrowLeft className="w-5 h-5 text-primary" /></a>
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <ScrollText className="w-5 h-5 text-primary" />
              </div>
              <h1
                className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary to-chart-1 bg-clip-text text-transparent"
                style={{ fontFamily: "Orbitron, sans-serif" }}
              >
                Términos y Condiciones
              </h1>
            </div>
          </div>

          <p className="text-muted-foreground mb-10 text-sm">
            Última actualización: 11 de mayo de 2026
          </p>

          <div className="space-y-10 text-muted-foreground leading-relaxed">

            {/* Sección 1 */}
            <Section title="1. Aceptación de los Términos">
              <p>
                Al acceder y utilizar los servicios de <strong className="text-foreground">SofIA</strong> ("la Empresa", "nosotros"), usted acepta quedar vinculado por estos Términos y Condiciones. Si no está de acuerdo con alguna parte de estos términos, no podrá acceder a nuestros servicios.
              </p>
              <p>
                Estos términos aplican a todos los visitantes, usuarios y cualquier otra persona que acceda o use nuestros servicios de agentes de inteligencia artificial.
              </p>
            </Section>

            {/* Sección 2 */}
            <Section title="2. Descripción del Servicio">
              <p>
                SofIA ofrece agentes de inteligencia artificial para uso personal y empresarial bajo tres planes de suscripción:
              </p>
              <ul className="list-disc list-inside space-y-1 mt-3 pl-2">
                <li><strong className="text-foreground">SofIA Básica</strong> — $249/mes: Asistente personal para uso cotidiano.</li>
                <li><strong className="text-foreground">SofIA Pro</strong> — $559/mes: Agente avanzado para profesionales.</li>
                <li><strong className="text-foreground">SofIA Empresarial</strong> — $1,499/mes: Solución completa para empresas.</li>
                <li><strong className="text-foreground">Servicios Adicionales</strong> — $299/hora: Consultoría y personalización.</li>
              </ul>
            </Section>

            {/* Sección 3 */}
            <Section title="3. Registro y Cuenta de Usuario">
              <p>
                Para utilizar nuestros servicios, deberá crear una cuenta proporcionando información precisa, completa y actualizada. Usted es responsable de mantener la confidencialidad de su contraseña y de todas las actividades que ocurran bajo su cuenta.
              </p>
              <p>
                Debe notificarnos de inmediato sobre cualquier uso no autorizado de su cuenta. SofIA no será responsable por pérdidas derivadas del uso no autorizado de su cuenta si usted no cumplió con esta obligación.
              </p>
            </Section>

            {/* Sección 4 */}
            <Section title="4. Facturación y Pagos">
              <p>
                Los planes de suscripción se facturan mensualmente por adelantado. El pago se procesa automáticamente en la fecha de renovación de su suscripción.
              </p>
              <p>
                Los precios están sujetos a cambios con un aviso previo de 30 días. Los cargos son en dólares estadounidenses (USD) e incluyen los impuestos aplicables según la jurisdicción del cliente.
              </p>
              <p>
                En caso de que un pago falle, la cuenta podrá suspenderse hasta que el pago sea procesado correctamente.
              </p>
            </Section>

            {/* Sección 5 */}
            <Section title="5. Período de Prueba y Cancelación">
              <p>
                Ofrecemos un período de prueba gratuito de <strong className="text-foreground">14 días</strong> para todos los planes. Durante este período, puede cancelar sin cargo alguno.
              </p>
              <p>
                Puede cancelar su suscripción en cualquier momento desde su panel de usuario. La cancelación será efectiva al final del período de facturación actual. No se realizarán reembolsos por períodos parciales.
              </p>
            </Section>

            {/* Sección 6 */}
            <Section title="6. Uso Aceptable">
              <p>Usted se compromete a no utilizar nuestros servicios para:</p>
              <ul className="list-disc list-inside space-y-1 mt-3 pl-2">
                <li>Actividades ilegales o fraudulentas.</li>
                <li>Generar contenido dañino, ofensivo o que infrinja derechos de terceros.</li>
                <li>Intentar acceder sin autorización a sistemas o redes.</li>
                <li>Distribuir malware, spam o contenido engañoso.</li>
                <li>Revender o sublicenciar el acceso al servicio sin autorización expresa.</li>
              </ul>
            </Section>

            {/* Sección 7 */}
            <Section title="7. Propiedad Intelectual">
              <p>
                Todos los derechos de propiedad intelectual sobre el servicio, incluyendo software, algoritmos, diseño, marcas y contenido, son propiedad exclusiva de SofIA o sus licenciantes.
              </p>
              <p>
                El contenido generado por los agentes de IA en respuesta a sus solicitudes le pertenece a usted, siempre que su uso sea conforme a estos términos.
              </p>
            </Section>

            {/* Sección 8 */}
            <Section title="8. Limitación de Responsabilidad">
              <p>
                SofIA no será responsable por daños indirectos, incidentales, especiales o consecuentes que resulten del uso o la imposibilidad de usar nuestros servicios, incluyendo pérdida de datos, ingresos o ganancias.
              </p>
              <p>
                En ningún caso nuestra responsabilidad total excederá el monto pagado por usted en los últimos 3 meses de servicio.
              </p>
            </Section>

            {/* Sección 9 */}
            <Section title="9. Disponibilidad del Servicio">
              <p>
                Nos comprometemos a mantener una disponibilidad del servicio del 99.5% mensual. Sin embargo, podemos interrumpir temporalmente el servicio por mantenimiento, actualizaciones o causas de fuerza mayor, notificando con la mayor anticipación posible.
              </p>
            </Section>

            {/* Sección 10 */}
            <Section title="10. Modificaciones a los Términos">
              <p>
                Nos reservamos el derecho de modificar estos términos en cualquier momento. Los cambios entrarán en vigor 30 días después de su publicación. El uso continuado del servicio después de dicho período constituirá su aceptación de los nuevos términos.
              </p>
            </Section>

            {/* Sección 11 */}
            <Section title="11. Ley Aplicable">
              <p>
                Estos términos se regirán e interpretarán de acuerdo con las leyes vigentes, sin perjuicio de sus disposiciones sobre conflictos de leyes. Cualquier disputa se resolverá mediante arbitraje vinculante o en los tribunales competentes.
              </p>
            </Section>

            {/* Sección 12 */}
            <Section title="12. Contacto">
              <p>
                Si tiene preguntas sobre estos Términos y Condiciones, puede contactarnos en:
              </p>
              <div className="mt-3 p-4 rounded-lg bg-primary/5 border border-primary/20">
                <p><strong className="text-foreground">SofIA</strong></p>
                <p>Email: legal@softservicesia.com</p>
                <p>Sitio web: www.softservicesia.com</p>
              </div>
            </Section>

          </div>
        </motion.div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      className="border-l-2 border-primary/30 pl-6"
    >
      <h2
        className="text-xl font-semibold text-foreground mb-4"
        style={{ fontFamily: "Orbitron, sans-serif" }}
      >
        {title}
      </h2>
      <div className="space-y-3">{children}</div>
    </motion.div>
  );
}
